import { EMPTY_DOC, type ArticleDoc } from '@archetelos/editor';
import { formatArticleDate } from '@archetelos/lib/date';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';

import type { ComposerState, GatePolicy } from './types';

import { contentQueries, type ContentVersion } from '@/lib/queries/content';

interface Props {
  contentId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRestore: (state: ComposerState) => void;
}

// Collapse consecutive autosaves within 5 minutes into the latest of the run.
function collapse(versions: ContentVersion[]): ContentVersion[] {
  const out: ContentVersion[] = [];
  for (const v of versions) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.is_autosave &&
      v.is_autosave &&
      Math.abs(new Date(prev.created_at).getTime() - new Date(v.created_at).getTime()) <
        5 * 60 * 1000
    ) {
      continue; // prev is newer (list is desc); skip this older autosave
    }
    out.push(v);
  }
  return out.slice(0, 10);
}

export function VersionHistoryModal({ contentId, open, onOpenChange, onRestore }: Props) {
  const versions = useQuery({
    ...contentQueries.versions(contentId),
    enabled: open && Boolean(contentId),
  });
  const list = collapse(versions.data ?? []);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[92vw] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ink/10 bg-parchment p-6 shadow-2xl">
          <Dialog.Title className="font-serif text-xl text-ink">Version history</Dialog.Title>
          <Dialog.Description className="mt-1 font-sans text-sm text-ink/60">
            The last ten saves. Consecutive autosaves are collapsed.
          </Dialog.Description>
          <ul className="mt-6 max-h-[50vh] space-y-2 overflow-y-auto">
            {list.length === 0 && (
              <li className="py-6 text-center text-sm text-ink/50">No saved versions yet.</li>
            )}
            {list.map((v) => (
              <li
                key={v.id}
                className="flex items-center justify-between rounded-md border border-ink/10 px-4 py-3"
              >
                <div>
                  <p className="font-sans text-sm text-ink">
                    {formatArticleDate(new Date(v.created_at))}{' '}
                    <span className="text-ink/40">
                      {new Date(v.created_at).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </p>
                  <p className="font-sans text-xs uppercase tracking-[0.12em] text-ink/50">
                    {v.is_autosave ? 'Autosave' : 'Manual save'}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        'Restore this version into the editor? Your current text becomes a new version on next save.',
                      )
                    ) {
                      onRestore({
                        title: v.title ?? '',
                        excerpt: v.excerpt ?? '',
                        slug: '',
                        bodyJson: (v.body_json as ArticleDoc | null) ?? EMPTY_DOC,
                        coverAssetId: v.cover_asset_id ?? null,
                        coverImageUrl: v.cover_image_url ?? '',
                        coverImageAlt: v.cover_image_alt ?? '',
                        traditionTags: v.tradition_tags ?? [],
                        topicTags: v.topic_tags ?? [],
                        gatePolicy: (v.gate_policy as GatePolicy) ?? 'free_14_days',
                        // access_tier isn't versioned; restore leaves it at the default.
                        accessTier: 'metered',
                      });
                      onOpenChange(false);
                    }
                  }}
                  className="rounded-md border border-ink/20 px-3 py-1.5 text-xs font-medium text-ink hover:border-oxblood hover:text-oxblood"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-6 text-right">
            <Dialog.Close className="rounded-md border border-ink/20 px-4 py-2 text-sm text-ink hover:border-oxblood">
              Close
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
